import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { hotjar } from 'react-hotjar';
import QueryString from 'query-string';
import Layout from './components/Layout';
import Home from './components/Home/Home';
import MCT from './components/Quoters/MCT/MCT';
import SecurianWL from './components/Quoters/Securian/WL/SecurianWL';
import LTC from './components/Quoters/LTC/LTC';
import Ineligible from './components/Quoters/Common/Ineligible';
import Ameritas from './components/Quoters/Ameritas/Ameritas';
import EsignReturn from './components/Quoters/Common/EsignReturn';
import ProductLandingPage from './components/LandingPages/ProductLandingPage';
import AppThemeProvider from './components/Theme/AppThemeProvider';
import { CircularProgress, StyledEngineProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import NewSession from './components/NewSession';
import {
  setBasicInfo,
  setSessionStorageItem,
  getSessionStorageItem,
  setSessionStorageItemWithExpiration,
  SESSION_STORAGE_ACCOUNT_KEY,
  SESSION_STORAGE_CLIENTINFO_KEY,
  SESSION_STORAGE_UTM_DATA,
  SESSION_STORAGE_EMAIL_CAPTURE,
  SESSION_STORAGE_SAVED_QUOTE_DATA,
  SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY,
} from './sessionStorage';
import { retrieveAccount } from './account';
import { retrieveConsumer } from './purlConsumer';
import { loadClientInfo } from './clientInfo';
import { saveSessionEvent } from './session';
import StateDisclosure from './components/Disclosure/StateDisclosure';
import Status from './components/Status/Status';
import Portal from './components/Portal/Portal';
import DidNotConsent from './components/Quoters/Securian/WL/DidNotConsent';
import Products from './components/Helpers/Data/Products/Products';
import RetrieveProgress from './components/General/RetrieveProgress';
import NoIeSupport from './components/Helpers/Errors/Templates/NoIeSupport';
import Medicare from './components/Quoters/Medicare/Medicare';
import Gerber from './components/Quoters/Forms/GerberForm';
import Supplemental from './components/Quoters/Supplemental/Supplemental';
import Account from './components/Account/Account';
import Landing from './components/PolicyReview/Landing';
import ExistingInsurance from './components/PolicyReview/ExistingInsurance';
import Schedule from './components/PolicyReview/Schedule';
import Summary from './components/PolicyReview/Summary';
import { detect } from 'detect-browser';
import Aflac from './components/Quoters/Aflac/Aflac';
import Digital from './components/Quoters/Digital/Digital';
import LongTerm from './components/Quoters/LongTerm/LongTerm';
import PartnerReferral from './components/PartnerReferral/PartnerReferral';
import CustomizeTerm from './components/Quoters/CustomizeTerm/CustomizeTerm';
import FinalExpense from './components/Quoters/FinalExpense/FinalExpense';
import WholeLife from './components/Quoters/WholeLife/WholeLife';
import UniversalLife from './components/Quoters/UniversalLife/UniversalLife';
import LoanProtection from './components/Quoters/AmericoLoanProtection/LoanProtection';
import PartnerLoanProtection from './components/Quoters/AmeicoPartnerLoanProtection/PartnerLoanProtection';
import AppointmentScheduler from './components/Scheduler/AppointmentScheduler';
import './Global.scss';

const browser = detect();
const isIe = browser && browser.name === 'ie';

const PageNotFound = () => {
  return (
    <div className="container py-5">
      <div className="bg-light py-5 px-4 rounded-3 shadow-sm">Page not found.</div>
    </div>
  );
};

let primaryColor = null;
let secondaryColor = null;

const setColorsFromAccount = (acc) => {
    primaryColor = acc ? acc.primaryColor : null;
    secondaryColor = acc ? acc.secondaryColor : null;
};

const parseUtm = (location) => {
  const queryString = QueryString.parse(location.search);
  return JSON.stringify(Object.entries(queryString)
    .filter((key) => key[0].substring(0, 4).toLowerCase() === "utm_").map((u) => {
      return u.reduce((k, v) => {
        return { [k]: v };
      });
    }),
  );
};

const App = () => {
  const [loadingSite, setLoadingSite] = useState(true);
  const [loadingClientInfo, setLoadingClientInfo] = useState(true);
  const [error, setError] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const initializeHotjar = (account) => {
    const utms = parseUtm(location);

    if (hotjar.initialized()) {
      hotjar.identify(null, {
        accountName: account.accountName,
        accountId: account.accountId,
        ...utms,
      });
    }
  };

  const redirectUtm = (acc) => {
    if (acc.accountRedirects) {
      const redirects = acc.accountRedirects
        .filter((redr) => redr.isEnabled)
        .reduce((obj, redr) => {
          obj[`${redr.utmKey}_${redr.utmValue}`] = redr.redirectUrl;
          return obj;
        }, {});
      const queryString = QueryString.parse(location.search);
      for (const key in queryString) {
        if (key.substring(0, 4).toLowerCase() === "utm_") {
          const queryStringUrl = `${key.substring(4)}_${queryString[key]}`.toLowerCase();
          const redirectUrl = redirects[queryStringUrl];
          if (redirectUrl) {
            navigate(redirectUrl);
          }
        }
      }
    }
  };

  const processPurl = (utm) => {
    const utmObj = JSON.parse(utm).find((g) => g.utm_guid !== undefined);

    const purlRedirect = (productType) => {
      let redirect;

      switch (productType) {
        case "terma":
          redirect = "/term-insurance/quote-results";
          break;
        case "termb":
          redirect = "/quote/term-life-insurance/multi-carrier-term/get-a-quote";
          break;
        case "securianwl":
          redirect = "/quote/whole-life/securian-secure-promise/get-a-quote";
          break;
        case "securiancwl":
          redirect = "/quote/whole-life/securian-secure-promise/get-a-quote?juvi";
          break;
        case "loanprotector":
          redirect = "/loan-protection/quote-results";
          break;
        case "loanprotectorexternal":
          redirect = "/partner-loan-protection/getting-started";
          break;
        case "supplemental":
          redirect = "/landing/supplemental";
          break;
        default:
          redirect = "/";
          break;
      }

      return redirect;
    };

    if (utmObj) {
      if (Object.keys(utmObj).length > 0) {
        setSessionStorageItem("ACTIVE_PURL", { "active": true });
      }

      retrieveConsumer(utmObj.utm_guid).then((consumer) => {
        const productType = !consumer.message
          ? consumer.productType.toLowerCase()
          : null;
        const redirectPath = purlRedirect(productType);
        const prefill = {
          firstName: "",
          lastName: "",
          gender: consumer.gender,
          birthdate: consumer.birthDate,
          fakeBirthdate: consumer.fakeBirthDate,
          state: consumer.state,
          tobacco:
            productType && consumer.productType.toLowerCase() === "terma"
              ? "false"
              : "Never",
          email: "",
          phone: null,
          faceAmount: consumer.coverageAmount,
          termLength: isNaN(parseInt(consumer.termLength))
            ? 30
            : parseInt(consumer.termLength),
          healthClass: "PP",
          height: "",
          weight: "",
          bmi: null,
          income: null,
          preQuoteAbbreviatedQualified: null,
          preQuoteHealthClass: null,
          replacePolicy: null,
          usResident: true,
        };

        setBasicInfo(prefill);

        navigate(redirectPath);
      });
    } else {
      setSessionStorageItem("ACTIVE_PURL", { "active": false });
    }
  };

  const setPartnerReferralEmailCaptureActive = (account) => {
    if (account.partnerReferralUtmKeys !== null) {
      const partnerReferralUtmKeys = account.partnerReferralUtmKeys.split(",");
      const utms = parseUtm(location);
      const utmObj = JSON.parse(utms);
  
      const utmKeys = utmObj.map((item) => {
        const key = Object.keys(item)[0];
        return key.split("_")[1];
      });
  
      const hasUtmMatch = partnerReferralUtmKeys.some((key) => utmKeys.includes(key));
  
      return hasUtmMatch;
    }
    return false;
  };

  useEffect(() => {
    const queryParams = QueryString.parse(location.search);
    const token = queryParams.token;

    if (token) {
      setSessionStorageItemWithExpiration(
        SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY,
        token,
      );

      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search]);

  const setEmailCapture = (account) => {
    const emailCaptureObj = getSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE);
    setSessionStorageItem(SESSION_STORAGE_EMAIL_CAPTURE, {
      ...emailCaptureObj,
      "enableInitialEmailCapture": account.enableInitialEmailCapture,
      "enableTimeoutEmailCapture": account.enableTimeoutEmailCapture,
      "enablePartnerReferralEmailCapture": account.enablePartnerReferralEmailCapture,
      "isPartnerReferralEmailCaptureActive": (!!emailCaptureObj && emailCaptureObj.isPartnerReferralEmailCaptureActive === true) ? 
        true : setPartnerReferralEmailCaptureActive(account),
    });
  };

  const setSaveQuoteData = () => {
    const savedQuoteObj = getSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA);
    setSessionStorageItem(SESSION_STORAGE_SAVED_QUOTE_DATA, {
      "quoteSaved": savedQuoteObj ? savedQuoteObj.quoteSaved : false,
      "emailAddress": savedQuoteObj ? savedQuoteObj.emailAddress : null,
      "firstName": savedQuoteObj ? savedQuoteObj.firstName : null,
      "lastName":  savedQuoteObj ? savedQuoteObj.lastName : null,
    });
  };

  const setFaviconUrl = (account) => {
    const iconUrl = (account && account.account && account.account.faviconUrl) || "/favicon.ico";
    let link = document.querySelector("link[rel~='shortcut icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = iconUrl;
  };

  useEffect(() => {
    saveSessionEvent();
  }, [location]);

  useEffect(() => {
    const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
    const utmData = parseUtm(location);
    const utmArray = JSON.parse(utmData);
    if (utmArray.length > 0) {
      const stringArray = utmArray.map((obj) => {
        const key = Object.keys(obj)[0];
        const value = obj[key];
        return `${key}=${value}`;
      });
      const resultString = stringArray.join(", ");
      setSessionStorageItem(SESSION_STORAGE_UTM_DATA, resultString);
    }

    if (!accountObj) {
      processPurl(utmData);
      retrieveAccount(utmData)
        .then((account) => {
          if (!account.accountProducts.length) {
            account.accountProducts = Products();
          }
          setSessionStorageItemWithExpiration(SESSION_STORAGE_ACCOUNT_KEY, account, 60);
          if (!account.accountOverrides) {
            setColorsFromAccount(account.account);
          } else {
            setColorsFromAccount(account.accountOverrides[0]);
          }
          setSaveQuoteData();
          setEmailCapture(account.account);
          initializeHotjar(account.account);
          redirectUtm(account);
          setFaviconUrl(account);
          setLoadingSite(false);
        })
        .catch((error) => {
          setError(true);
          setLoadingSite(false);
          return;
        });
    } else {
      if (!accountObj.accountOverrides) {
        setColorsFromAccount(accountObj.account);
      } else {
        setColorsFromAccount(accountObj.accountOverrides[0]);
      }
      setSaveQuoteData();
      setEmailCapture(accountObj.account);
      initializeHotjar(accountObj.account);
      redirectUtm(accountObj);
      setFaviconUrl(accountObj);
      setLoadingSite(false);
    }
    
    if (window.location.href.includes("DataImport")) {
      setLoadingClientInfo(true);
      loadClientInfo().then((clientInfo) => {
        if (clientInfo !== undefined) {
          setSessionStorageItemWithExpiration(SESSION_STORAGE_CLIENTINFO_KEY, clientInfo, 60);
        }
        setLoadingClientInfo(false);
      }).catch((error) => {
        setLoadingClientInfo(false);
      });
    }
    else {
      setLoadingClientInfo(false);
    }
  }, []);

  if (isIe) {
    return (
      <div>
        <NoIeSupport />
      </div>
    );
  }
  return (
    <div>
      {loadingSite === false &&
        loadingClientInfo === false &&
        error === false && (
          <HelmetProvider>
            <StyledEngineProvider injectFirst>
              <AppThemeProvider primaryColor={primaryColor} secondaryColor={secondaryColor}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Layout>
                    <Routes>
                      <Route exact path="/" element={<Home />} />

                      <Route path="/RetrieveProgress" element={<RetrieveProgress />} />

                      <Route path="/Portal" element={<Portal />} />

                      <Route path="/landing/:product" element={<ProductLandingPage />} />
                      <Route path="/landing/term/digital-term" element={<ProductLandingPage />} />

                      <Route path="/gl" element={<Gerber />} />

                      <Route path="/select-appointment" element={<AppointmentScheduler />} />
                      <Route path="/select-appointment/:path" element={<AppointmentScheduler />} />

                      <Route path="/quote/term-life-insurance/multi-carrier-term" element={<MCT />} />
                      <Route path="/quote/term-life-insurance/multi-carrier-term/:path" element={<MCT />} />
                      <Route path="/quote/term-life-insurance/multi-carrier-term/schedule-appointment/:path" element={<MCT />} />

                      <Route path="/whole-life" element={<WholeLife />} />
                      <Route path="/whole-life/:path" element={<WholeLife />} />

                      <Route path="/universal-life" element={<UniversalLife />} />
                      <Route path="/universal-life/:path" element={<UniversalLife />} />

                      <Route path="/term-insurance" element={<CustomizeTerm />} />
                      <Route path="/term-insurance/:path" element={<CustomizeTerm />} />

                      <Route path="/final-expense" element={<FinalExpense />} />
                      <Route path="/final-expense/:path" element={<FinalExpense />} />

                      <Route path="/loan-protection/EsignReturn" element={<EsignReturn />} />
                      <Route path="/loan-protection/:path" element={<LoanProtection />} />
                      <Route path="/loan-protection" element={<LoanProtection />} />

                      <Route path="/partner-loan-protection/EsignReturn" element={<EsignReturn />} />
                      <Route path="/partner-loan-protection/:path" element={<PartnerLoanProtection />} />
                      <Route path="/partner-loan-protection" element={<PartnerLoanProtection />} />

                      <Route path="/quote/term-life-insurance/fully-digital" element={<Digital />} />
                      <Route path="/quote/term-life-insurance/fully-digital/:path" element={<Digital />} />

                      <Route path="/quote/term-life-insurance/:path" element={<MCT />} />

                      <Route path="/quote/whole-life/securian-secure-promise/EsignReturn" element={<EsignReturn />} />
                      <Route path="/quote/whole-life/securian-secure-promise/:path" element={<SecurianWL />} />
                      <Route path="/quote/whole-life/securian-secure-promise" element={<SecurianWL />} />

                      <Route path="/quote/long-term-care" element={<LTC />} />
                      <Route path="/quote/long-term-care/:path" element={<LTC />} />
                      <Route path="/quote/long-term-care/schedule-appointment/:path" element={<LTC />} />
                      
                      <Route path="/quote/disability/ameritas-dinamic-Fundamental/:path" element={<Ameritas />} />
                      <Route path="/quote/disability/ameritas-dinamic-Fundamental/" element={<Ameritas />} />
                      <Route path="/quote/disability/long-term/:path" element={<LongTerm />} />
                      <Route path="/quote/disability/long-term/" element={<LongTerm />} />
                      <Route path="/quote/term-health-insurance/medicare/:path" element={<Medicare />} />
                      <Route path="/quote/term-health-insurance/medicare/" element={<Medicare />} />
                      <Route path="/quote/supplemental/:path" element={<Supplemental />} />
                      <Route path="/quote/supplemental/schedule-appointment/:path" element={<Supplemental />} />

                      <Route path="/quote/aflac/hospital-confinement/EsignReturn" element={<EsignReturn />} />
                      <Route path="/quote/aflac/hospital-confinement/:path" element={<Aflac />} />
                      <Route path="/quote/aflac/hospital-confinement/" element={<Aflac />} />

                      <Route path="/Ineligible" element={<Ineligible />} />
                      <Route path="/StateDisclosure" element={<StateDisclosure />} />
                      <Route path="/DidNotConsent" element={<DidNotConsent />} />

                      <Route path="/status" element={<Status />} />
                      <Route path="/Action/Status" element={<Status />} />
                      <Route path="/Action/SignNow" element={<Status />} />

                      <Route path="/account/:path/:applicationId" element={<Account />} />
                      <Route path="/account/:path" element={<Account />} />

                      <Route path="/policy-review" element={<Landing />} exact />
                      <Route path="/policy-review/existing-insurance" element={<ExistingInsurance />} />
                      <Route path="/policy-review/schedule" element={<Schedule />} />
                      <Route path="/policy-review/summary" element={<Summary />} />

                      <Route path="/partner-referral" element={<PartnerReferral />} />

                      <Route path="/ns" element={<NewSession />} />
                      <Route path="*" element={<PageNotFound />} />

                    </Routes>
                  </Layout>
                </LocalizationProvider>
              </AppThemeProvider>
            </StyledEngineProvider>
          </HelmetProvider>
        )}

      {loadingSite && (
        <div className="d-flex justify-content-center mt-4">
          <CircularProgress />
        </div>
      )}

      {error && (
        <div className="d-flex justify-content-center mt-4">
          <p>The account you are trying to access does not exist. Please check the URL and try again.</p>
        </div>
      )}
    </div>
  );
};

export default App;
